"use client";

import React, { useMemo, useState } from "react";
import Image, { type StaticImageData } from "next/image";
import { Plus, Check } from "lucide-react";
import ContactForm from "./ContactForm";
import LinkButton from "@/components/ui/LinkButton";

type Bullet = string | React.ReactNode;
type SectionVariant = "white" | "gray1";

interface Props {
  productName: string;

  hero: {
    title: string;
    subtitle: string;
    bullets?: Bullet[];
    buttonLeft: { title: string; href: string };
    buttonRight: { title: string; href: string };
    image?: StaticImageData | string;
    images?: {
      src: StaticImageData;
      alt: string;
      title: string;
      desc: string;
    }[];
    imageAlt: string;
    imageFit?: "cover" | "contain";
  };

  details?: {
    paragraphs: Bullet[];
  };
  spacer?: React.ReactNode;

  highlight: {
    image: StaticImageData | string;
    bullets?: Bullet[];
    html?: React.ReactNode;
  };

  video?: { title: string; src: string };
  beforeVideo?: React.ReactNode;
  children?: React.ReactNode;

  faqs: { q: string; a: string }[];

  features: {
  id?: string;
  content?: React.ReactNode;
  src?: StaticImageData;
  alt?: string;
  title?: string;
  desc?: string;
}[];
}
[];

function normalizeHyphens(text: string) {
  return text.replace(/[\u2013\u2014]/g, "-");
}

const variantClass: Record<SectionVariant, string> = {
  white: "bg-white",
  gray1: "bg-gray-100",
};

export default function ProductPage(props: Props) {
  const heroImg =
    props.hero.image ?? props.hero.images?.[0]?.src ?? props.highlight.image;

  const heroSubtitle = useMemo(
    () => normalizeHyphens(props.hero.subtitle),
    [props.hero.subtitle],
  );

  const hasDetails = !!props.details?.paragraphs?.length;
  const hasBeforeVideo = !!props.beforeVideo;
  const hasVideo = !!props.video;
  const hasSpacer = !!props.spacer;
  const hasChildren = !!props.children;
  const hasHighlight = !!props.highlight?.html;

  const variants = useMemo(() => {
    const list: SectionVariant[] = ["white", "gray1"];
    let i = 0;
    const next = () => list[i++ % 2] as SectionVariant;
    return {
      details: hasDetails ? next() : null,
      beforeVideo: hasBeforeVideo ? next() : null,
      video: hasVideo ? next() : null,
      spacer: hasSpacer ? next() : null,
      children: hasChildren ? next() : null,
      highlight: hasHighlight ? next() : null,
      faq: next(),
      contact: next(),
    };
  }, [
    hasDetails,
    hasBeforeVideo,
    hasVideo,
    hasSpacer,
    hasChildren,
    hasHighlight,
  ]);

  return (
    <main className="min-h-screen">
      {/* HERO */}
      <section className="w-full">
        <div className="grid grid-cols-1 lg:grid-cols-2 items-stretch min-h-150">
          <div className="relative w-full overflow-hidden aspect-video lg:aspect-auto lg:min-h-150">
            <Image
              src={heroImg!}
              alt={props.hero.imageAlt ?? props.productName}
              className={`w-full h-full ${props.hero.imageFit === "contain" ? "object-contain object-top" : "object-cover"}`}
              fill
              priority
              sizes="(max-width: 1024px) 100vw, 50vw"
            />
          </div>

          <div className="flex items-start h-full pt-8 lg:pt-10">
            <div className="w-full px-8 sm:px-12 lg:px-14">
              <div className="max-w-xl">
                <h1 className="text-[28px] sm:text-[34px] lg:text-[40px] font-normal leading-[1.15] text-slate-900">
                  {normalizeHyphens(props.hero.title)}
                </h1>

                <p className="mt-3 text-[15px] sm:text-[16px] text-slate-700 leading-relaxed">
                  {heroSubtitle}
                </p>

                {(props.hero.bullets ?? props.highlight?.bullets ?? []).length >
                  0 && (
                  <ul className="mt-5 text-slate-900 text-[15px] sm:text-[16px] flex flex-col gap-3">
                    {(props.hero.bullets ?? props.highlight?.bullets ?? []).map(
                      (b, i) => (
                        <li key={i} className="flex items-start gap-3">
                          <Check className="shrink-0 mt-1 w-5 h-5 text-brand-primary" />
                          <span className="font-semibold [&>strong]:font-semibold">
                            {b}
                          </span>
                        </li>
                      ),
                    )}
                  </ul>
                )}

                <div className="mt-7 flex flex-col sm:flex-row gap-4">
                  <LinkButton
                    href={props.hero.buttonLeft.href}
                    variant="primary"
                    className="h-12 px-8 text-[15px] sm:min-w-55 rounded-full"
                  >
                    {props.hero.buttonLeft.title}
                  </LinkButton>

                  <LinkButton
                    href={props.hero.buttonRight.href}
                    variant="outline"
                    className="h-12 px-8 text-[15px] sm:min-w-55 rounded-full"
                  >
                    {props.hero.buttonRight.title}
                  </LinkButton>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* DETAILS / INTRO */}
      {props.details?.paragraphs?.length ? (
        <section className={`w-full ${variantClass[variants.details!]}`}>
          <div className="mx-auto max-w-5xl px-4 py-8 sm:py-10">
            <div className="mx-auto max-w-3xl text-center">
              <div className="space-y-4 text-slate-900 text-lg sm:text-xl leading-relaxed">
                {props.details.paragraphs.map((p, i) => (
                  <p key={i}>
                    {typeof p === "string" ? normalizeHyphens(p) : p}
                  </p>
                ))}
              </div>
            </div>
          </div>
        </section>
      ) : null}

      {/* BEFORE VIDEO */}
      {props.beforeVideo && (
        <div className={variantClass[variants.beforeVideo!]}>
          {props.beforeVideo}
        </div>
      )}

      {/* VIDEO */}
      {props.video ? (
        <section className={`w-full ${variantClass[variants.video!]}`}>
          <div className="mx-auto max-w-5xl px-4 py-10 sm:py-12">
            <div className="text-center">
              <h2 className="text-xl sm:text-2xl font-semibold tracking-tight text-slate-900">
                {normalizeHyphens(props.video.title)}
              </h2>
            </div>
            <div className="mt-6">
              <div className="relative aspect-video overflow-hidden rounded-2xl ring-1 ring-black/10 bg-black shadow-sm">
                <video className="h-full w-full" controls>
                  <source src={props.video.src} type="video/mp4" />
                </video>
              </div>
            </div>
          </div>
        </section>
      ) : null}

      {/* SPACER */}
      {props.spacer && (
        <section className={`w-full ${variantClass[variants.spacer!]}`}>
          <div className="mx-auto max-w-5xl px-4 py-8 sm:py-10">
            <div className="mx-auto max-w-3xl text-center text-slate-900 text-lg sm:text-xl leading-relaxed">
              {props.spacer}
            </div>
          </div>
        </section>
      )}

      {/* CUSTOM CONTENT (children) */}
      {props.children && (
        <div className={variantClass[variants.children!]}>{props.children}</div>
      )}

      {/* HIGHLIGHT */}
      {props.highlight?.html && (
        <section className={`w-full ${variantClass[variants.highlight!]}`}>
          <div className="mx-auto max-w-6xl px-4 py-14 sm:py-16">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>{props.highlight.html}</div>
              <div className="relative">
                <Image
                  src={props.highlight.image}
                  alt="Produkt Highlight"
                  className="rounded-2xl shadow-lg"
                  {...(typeof props.highlight.image === "string"
                    ? { fill: true }
                    : {})}
                />
              </div>
            </div>
          </div>
        </section>
      )}
      {/* FEATURES */}
      {props.features?.map((feature) => (
        <div key={feature.id ?? Math.random()}>{feature.content}</div>
      ))}
      {/* FAQ */}
      <section className={`w-full ${variantClass[variants.faq]}`}>
        <div className="mx-auto max-w-5xl px-4 py-10 sm:py-12">
          <div className="text-center">
            <h2 className="text-xl sm:text-2xl font-semibold tracking-tight text-slate-900">
              Häufige Fragen
            </h2>
          </div>
          <FaqAccordion faqs={props.faqs} />
        </div>
      </section>

      {/* FINAL CTA */}
      <div className={variantClass[variants.contact]}>
        <ContactForm product={props.productName} />
      </div>
    </main>
  );
}

function FaqAccordion({ faqs }: { faqs: { q: string; a: string }[] }) {
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  return (
    <div className="mt-7 grid grid-cols-1 gap-3 sm:gap-4">
      {faqs.map((f, idx) => {
        const isOpen = openIndex === idx;

        return (
          <div
            key={idx}
            className="rounded-2xl bg-white ring-1 ring-slate-200 shadow-sm transition-all duration-200 ease-out hover:shadow-md hover:ring-slate-300"
          >
            <button
              type="button"
              onClick={() => setOpenIndex(isOpen ? null : idx)}
              aria-expanded={isOpen}
              className="w-full flex items-center justify-between gap-4 p-4 sm:p-5 text-left"
            >
              <span className="text-sm sm:text-base font-semibold text-slate-900">
                {normalizeHyphens(f.q)}
              </span>

              <span
                className={`shrink-0 grid place-items-center h-8 w-8 rounded-full ring-1 ring-slate-200 transition-all duration-200 ease-out ${
                  isOpen ? "bg-brand-primary ring-brand-primary" : "bg-white"
                }`}
              >
                <Plus
                  className={`h-4 w-4 transition-transform duration-200 ease-out ${
                    isOpen
                      ? "rotate-45 text-white"
                      : "rotate-0 text-brand-primary"
                  }`}
                />
              </span>
            </button>

            <div
              className={`grid transition-[grid-template-rows,opacity] duration-200 ease-out px-4 sm:px-5 ${
                isOpen
                  ? "grid-rows-[1fr] opacity-100 pb-4 sm:pb-5"
                  : "grid-rows-[0fr] opacity-0 pb-0"
              }`}
            >
              <div className="overflow-hidden">
                <p className="text-slate-700 text-sm leading-relaxed">
                  {normalizeHyphens(f.a)}
                </p>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
